import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { ChevronsUpDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { formatUtils } from '@/lib/utils';
import { AdminClient, isNil } from '@activepieces/shared';

const CLIENT_PAGE_SIZE = 50;

/** Only the server query waits; local filtering stays on every keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Client numbers spelled out in the trigger before it falls back to a `+n`
 * counter.
 */
const MAX_CLIENTS_IN_TRIGGER = 2;

/**
 * Selected rows rendered in the "Selected" tab. The picker is
 * one-click-at-a-time so a selection this large is already unlikely — but
 * rendering an unbounded list inside a popover is the kind of thing that only
 * breaks in front of the one administrator who has it.
 */
const MAX_SELECTED_ROWS = 100;

const TAB = {
  ALL: 'all',
  SELECTED: 'selected',
} as const;

type ClientTab = (typeof TAB)[keyof typeof TAB];

type ClientMultiComboboxProps = {
  value: string[];
  onChange: (tenantIds: string[]) => void;
  /**
   * Name and client number already known for the ids in `value` — from the
   * definition being edited, which carries both. Without it the field can only
   * describe the clients that happen to be on the currently-loaded page, and
   * falls back to the raw tenant id for the rest.
   */
  knownClients?: Record<string, KnownClient>;
  disabled?: boolean;
};

const clientName = (client: AdminClient): string =>
  client.companyName ?? client.name;

/**
 * The Kizeo customer number, falling back to the tenant id: the number is what
 * an administrator identifies a client by, but a tenant that never came from
 * Kizeo has none, and an empty slot beside a name reads as a bug.
 */
const clientNumber = (client: AdminClient): string =>
  client.externalCustomerId ?? client.id;

/** What the field needs about a client, for ids off the loaded page. */
type KnownClient = {
  name: string;
  number: string;
};

/**
 * Picks the clients a Swagger is assigned to. An empty selection is a
 * first-class choice rather than an empty state: an unassigned Swagger is
 * visible to nobody, which is the safe default while a definition is still
 * being prepared.
 *
 * The selection lives in its own tab rather than as a badge row underneath.
 * Badges read well for two or three clients and collapse past a dozen, and the
 * field has to stay legible for an administrator who works across thousands of
 * them — hence "All clients" and "Selected" side by side, each with its count,
 * so the size of the selection is readable without opening anything.
 */
const ClientMultiCombobox = ({
  value,
  onChange,
  knownClients,
  disabled,
}: ClientMultiComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [tab, setTab] = useState<ClientTab>(TAB.ALL);

  // Keyed on the debounced term, not the raw input: the key is what makes React
  // Query refetch, so keying it on every keystroke fires one request per
  // character typed.
  const { data, isLoading } = useQuery({
    queryKey: ['admin-clients-combobox', debouncedSearch],
    queryFn: () =>
      adminApi.listClients({
        limit: CLIENT_PAGE_SIZE,
        ...(debouncedSearch.trim().length > 0
          ? { search: debouncedSearch.trim() }
          : {}),
      }),
    enabled: open,
  });

  const clients = useMemo(() => data?.data ?? [], [data]);
  const totalCount = data?.totalCount;

  // Accumulated across searches: a client selected under one search term is no
  // longer in `clients` once the term changes, but its name and number are
  // still needed to describe it in the trigger and in the "Selected" tab.
  const [discovered, setDiscovered] = useState<Record<string, KnownClient>>({});

  useEffect(() => {
    if (clients.length === 0) {
      return;
    }
    setDiscovered((current) => {
      const next = { ...current };
      let changed = false;
      for (const client of clients) {
        const name = clientName(client);
        const number = clientNumber(client);
        if (
          next[client.id]?.name !== name ||
          next[client.id]?.number !== number
        ) {
          next[client.id] = { name, number };
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [clients]);

  const nameOf = useCallback(
    (tenantId: string): string =>
      discovered[tenantId]?.name ?? knownClients?.[tenantId]?.name ?? tenantId,
    [discovered, knownClients],
  );

  const numberOf = useCallback(
    (tenantId: string): string =>
      discovered[tenantId]?.number ??
      knownClients?.[tenantId]?.number ??
      tenantId,
    [discovered, knownClients],
  );

  // Client numbers, not names: the closed field has to be readable against a
  // support ticket or the clients table, both of which speak in numbers. The
  // names stay one click away, in the list itself.
  const summary = useMemo(() => {
    if (value.length === 0) {
      return null;
    }
    return {
      numbers: value
        .slice(0, MAX_CLIENTS_IN_TRIGGER)
        .map((tenantId) => numberOf(tenantId))
        .join(', '),
      overflow: value.length - MAX_CLIENTS_IN_TRIGGER,
    };
  }, [value, numberOf]);

  // The "Selected" tab filters what is already in hand rather than asking the
  // server again: the ids are known, and a selected client has to stay
  // reachable even when the search term no longer matches the loaded page.
  const selected = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length === 0) {
      return value;
    }
    return value.filter((tenantId) =>
      `${nameOf(tenantId)} ${numberOf(tenantId)} ${tenantId}`
        .toLowerCase()
        .includes(term),
    );
  }, [value, search, nameOf, numberOf]);

  const toggle = (tenantId: string) => {
    onChange(
      value.includes(tenantId)
        ? value.filter((id) => id !== tenantId)
        : [...value, tenantId],
    );
  };

  const clientRow = (tenantId: string, name: string, number: string) => (
    <CommandItem
      key={tenantId}
      value={tenantId}
      disabled={disabled}
      onSelect={() => toggle(tenantId)}
      className="gap-3 px-2 py-2"
    >
      <Checkbox
        checked={value.includes(tenantId)}
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none"
      />
      <span className="min-w-0 flex-1 truncate">{name}</span>
      <span className="shrink-0 font-mono text-xs text-muted-foreground">
        {number}
      </span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between gap-2 font-normal"
        >
          {summary === null ? (
            <span className="text-muted-foreground">
              {t('No client selected')}
            </span>
          ) : (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-mono">{summary.numbers}</span>
              {summary.overflow > 0 && (
                <span className="shrink-0 text-muted-foreground">
                  {t('+{count}', { count: summary.overflow })}
                </span>
              )}
            </span>
          )}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-[24rem] p-0"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('Search by client name or ID')}
            value={search}
            onValueChange={setSearch}
          />
          <Tabs
            value={tab}
            onValueChange={(next) => setTab(next as ClientTab)}
            className="flex flex-col"
          >
            <div className="flex items-center justify-between gap-2 border-b px-3">
              <TabsList variant="outline" className="gap-4">
                <TabsTrigger
                  variant="outline"
                  value={TAB.ALL}
                  className="gap-2 px-0 data-[state=active]:border-primary data-[state=active]:text-primary"
                >
                  {t('All clients')}
                  {!isNil(totalCount) && (
                    <span className="text-xs text-muted-foreground">
                      {formatUtils.formatNumber(totalCount)}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  variant="outline"
                  value={TAB.SELECTED}
                  className="gap-2 px-0 data-[state=active]:border-primary data-[state=active]:text-primary"
                >
                  {t('Selected')}
                  <span className="text-xs text-muted-foreground">
                    {value.length}
                  </span>
                </TabsTrigger>
              </TabsList>
              {value.length > 0 && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange([])}
                  className="shrink-0 text-xs font-medium text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
                >
                  {t('Deselect All')}
                </button>
              )}
            </div>
            {/* The shared CommandList clips its overflow instead of scrolling
                it, which would make everything past the cap unreachable. */}
            <CommandList className="max-h-[16rem] overflow-y-auto">
              <TabsContent value={TAB.ALL} className="mt-0">
                {isLoading && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t('Loading...')}
                  </div>
                )}
                {/* Not `CommandEmpty`: cmdk only renders it off its own
                    filtering, which is disabled here — the search runs
                    server-side. */}
                {!isLoading && clients.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t('No results found.')}
                  </div>
                )}
                <CommandGroup>
                  {clients.map((client) =>
                    clientRow(
                      client.id,
                      clientName(client),
                      clientNumber(client),
                    ),
                  )}
                </CommandGroup>
                {!isNil(totalCount) && totalCount > clients.length && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {t(
                      'Showing {shown} of {total} — search to reach the rest',
                      {
                        shown: clients.length,
                        total: formatUtils.formatNumber(totalCount),
                      },
                    )}
                  </p>
                )}
              </TabsContent>
              <TabsContent value={TAB.SELECTED} className="mt-0">
                {selected.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {value.length === 0
                      ? t('No client selected')
                      : t('No results found.')}
                  </div>
                )}
                <CommandGroup>
                  {selected
                    .slice(0, MAX_SELECTED_ROWS)
                    .map((tenantId) =>
                      clientRow(tenantId, nameOf(tenantId), numberOf(tenantId)),
                    )}
                </CommandGroup>
                {selected.length > MAX_SELECTED_ROWS && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {t('and {count} more', {
                      count: selected.length - MAX_SELECTED_ROWS,
                    })}
                  </p>
                )}
              </TabsContent>
            </CommandList>
          </Tabs>
          <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
            <span className="text-xs text-muted-foreground">
              {value.length === 0
                ? t('No client selected')
                : t(
                    '{count, plural, one {# client selected} other {# clients selected}}',
                    { count: value.length },
                  )}
            </span>
            <Button
              type="button"
              size="sm"
              onClick={() => setOpen(false)}
              className="px-5"
            >
              {t('Done')}
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { ClientMultiCombobox };
export type { KnownClient };
